import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Tabs,
  Tab,
  Button,
  Divider,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { Loader } from "components/Loader";
import { CustomAutoComplete } from "components/CustomComponents";
import {
  leavePalette,
  cardSx,
  typeMeta,
  statusMeta,
} from "../MyZoneApply/Leave/leaveHelpers";
import { LeaveHistoryTable } from "../MyZoneApply/Leave/LeaveHistoryTable";
import { PermissionsDetails } from "./permissionDetails";
import { useLeaveDetailsData, toApiDate } from "./useLeaveDetailsData";

// Redesigned "Leave Details" screen (My Zone > View Leave Balance target,
// /admin/myzone/myleaves). Kept for deep links; the primary entry points (Home's
// "Leave Balance" quick link and the Leave screen's "Leave Balance Details" link)
// now open the LeaveDetailsDrawer instead. Both share useLeaveDetailsData, so the
// data and derived values are identical wherever the details appear.
// Tabs: Summary (balances + at-a-glance stats + recent history), Permissions
// (the existing PermissionsDetails table), History (full leave history).

const StatRow = ({ icon: Icon, color, label, value, valueColor, sub }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ py: 1.25 }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}1A`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant="body2" sx={{ color: leavePalette.subText }}>
        {label}
      </Typography>
    </Stack>
    <Box sx={{ textAlign: "right" }}>
      <Typography fontWeight={700} sx={{ color: valueColor || leavePalette.text }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" sx={{ color: leavePalette.amber }}>
          {sub}
        </Typography>
      ) : null}
    </Box>
  </Stack>
);

export const LeaveDetailsScreen = () => {
  const { userDetails } = useSelector((state) => state.tokenReducer);
  const history = useHistory();

  const {
    selectedYear,
    setSelectedYear,
    leaveCount,
    leaveTypes,
    loading,
    leaveRequests,
    totals,
    expiry,
    recentHistory,
    yearOptions,
    selectedYearLabel,
    displayName,
  } = useLeaveDetailsData();

  const [tab, setTab] = useState(0);

  // Permissions tab plumbing (the existing PermissionsDetails owns its own
  // fetch; it just needs the user + the selected window + these two setters).
  const [loadPermissionApi, setLoadPersmissionApi] = useState(true);
  const [totalPermission, setTotalPermission] = useState(0);

  // Re-enable the permissions fetch whenever the selected year changes.
  useEffect(() => {
    if (selectedYear) setLoadPersmissionApi(true);
  }, [selectedYear]);

  if (loading && !leaveCount) return <Loader info="Loading..." />;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: leavePalette.pageBg, minHeight: "100%" }}>
      {/* Identity header */}
      <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <RequestDetailHeader
              status="Active"
              userDetails={{
                image_url: leaveCount?.image_url || userDetails?.image_url,
                user_name: displayName,
                employee_id: leaveCount?.employee_id || userDetails?.employee_id || "-",
                department_name:
                  leaveCount?.department_name || userDetails?.department_name || "-",
                designation:
                  leaveCount?.designation || userDetails?.designation || "-",
              }}
            />
          </Box>

          <Box sx={{ minWidth: { xs: "100%", sm: 280 } }}>
            <CustomAutoComplete
              dataList={yearOptions}
              selectedValue={selectedYearLabel}
              updateDataFunction={(event, value) => {
                if (value) {
                  setSelectedYear({
                    name: value.name,
                    start_date: value.start_date,
                    end_date: value.end_date,
                  });
                  setTab(0);
                }
              }}
              placeHolder="Select leave year"
              fullWidth
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          border: `1px solid ${leavePalette.border}`,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: leavePalette.card,
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{
            px: { xs: 1.5, md: 2.5 },
            pt: 1.5,
            minHeight: 40,
            borderBottom: `1px solid ${leavePalette.border}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              minHeight: 40,
              color: leavePalette.subText,
            },
            "& .Mui-selected": { color: `${leavePalette.green} !important` },
            "& .MuiTabs-indicator": { backgroundColor: leavePalette.green },
          }}
        >
          <Tab label="Summary" />
          <Tab label="Permissions" />
          <Tab label="History" />
        </Tabs>
        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* SUMMARY */}
      {tab === 0 && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={7}>
            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography fontWeight={700} sx={{ color: leavePalette.text }}>
                  Leave Balances
                </Typography>
                <Typography variant="caption" sx={{ color: leavePalette.subText }}>
                  (In Days)
                </Typography>
              </Stack>

              <Grid container spacing={1.5}>
                {leaveTypes.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: leavePalette.subText }}>
                      No leave types configured.
                    </Typography>
                  </Grid>
                ) : (
                  leaveTypes.map((lt, idx) => {
                    const meta = typeMeta(lt.leave_type);
                    const entitled = Number(lt.credited_leave) || 0;
                    const used = Number(lt.used_leave) || 0;
                    const available = entitled - used;
                    return (
                      <Grid item xs={6} sm={3} key={lt.leave_type || idx}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            bgcolor: `${meta.color}14`,
                            border: `1px solid ${meta.color}33`,
                            height: "100%",
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ color: meta.color, display: "block", mb: 0.5 }}
                          >
                            {lt.leave_type}
                          </Typography>
                          <Typography fontWeight={800} sx={{ color: leavePalette.text, fontSize: 22, lineHeight: 1 }}>
                            {available}
                          </Typography>
                          <Typography variant="caption" sx={{ color: leavePalette.subText }}>
                            of {entitled}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <StatRow
                icon={EventAvailableIcon}
                color={leavePalette.green}
                label="Total Available"
                value={`${totals.available} days`}
              />
              <StatRow
                icon={AccessTimeIcon}
                color={leavePalette.amber}
                label="Expiring On"
                value={expiry.label}
                sub={
                  expiry.daysLeft !== null ? `(${expiry.daysLeft} days left)` : null
                }
              />
              <StatRow
                icon={BeachAccessIcon}
                color={leavePalette.blue}
                label="Leave Taken This Year"
                value={`${totals.used} days`}
              />
              <StatRow
                icon={FlightTakeoffIcon}
                color={leavePalette.blue}
                label="Upcoming Leave"
                value={`${totals.upcoming} days`}
                valueColor={leavePalette.blue}
              />

              <Button
                variant="outlined"
                fullWidth
                onClick={() => setTab(2)}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  borderRadius: 2,
                  borderColor: leavePalette.border,
                  color: leavePalette.text,
                  fontWeight: 600,
                  "&:hover": { borderColor: leavePalette.green, color: leavePalette.green },
                }}
              >
                View Full Year Summary
              </Button>
            </Box>
          </Grid>

          {/* Recent history + apply */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography fontWeight={700} sx={{ color: leavePalette.text }}>
                  Recent Leave History
                </Typography>
                <Typography
                  variant="caption"
                  onClick={() => setTab(2)}
                  sx={{ color: leavePalette.blue, fontWeight: 600, cursor: "pointer" }}
                >
                  View all
                </Typography>
              </Stack>

              {recentHistory.length === 0 ? (
                <Typography variant="body2" sx={{ color: leavePalette.subText, py: 2 }}>
                  No leave requests found.
                </Typography>
              ) : (
                recentHistory.map((row, idx) => {
                  const meta = typeMeta(row.leave_type);
                  const Icon = meta.icon;
                  const color = statusMeta(row.status, row.color_code);
                  return (
                    <Stack
                      key={row.id ?? idx}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        py: 1.1,
                        borderBottom:
                          idx !== recentHistory.length - 1
                            ? `1px solid ${leavePalette.border}`
                            : "none",
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1.5,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: `${meta.color}1A`,
                          color: meta.color,
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} sx={{ color: leavePalette.text, fontSize: 13 }}>
                          {row.leave_type || "—"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: leavePalette.subText }}>
                          {row.from_date}
                          {row.to_date && row.to_date !== row.from_date
                            ? ` – ${row.to_date}`
                            : ""}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: leavePalette.subText, whiteSpace: "nowrap" }}>
                        {row.business_days ?? "—"}{" "}
                        {Number(row.business_days) === 1 ? "Day" : "Days"}
                      </Typography>
                      <Box
                        sx={{
                          px: 1.2,
                          py: 0.3,
                          borderRadius: 5,
                          fontSize: 11,
                          fontWeight: 700,
                          color,
                          bgcolor: `${color}1A`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.status || "—"}
                      </Box>
                    </Stack>
                  );
                })
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={() => history.push("/admin/myzone/apply")}
                sx={{
                  mt: 2,
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: leavePalette.green,
                  "&:hover": { bgcolor: "#0EA05B" },
                  fontWeight: 600,
                }}
              >
                Apply Leave
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* PERMISSIONS */}
      {tab === 1 && (
        <Box sx={{ ...cardSx, p: { xs: 1, md: 2 } }}>
          <PermissionsDetails
            selectedUser={{ user_id: userDetails?.id }}
            loadPermissionApi={loadPermissionApi}
            setLoadPersmissionApi={setLoadPersmissionApi}
            setTotalPermission={setTotalPermission}
            taxYearStartDateUser={toApiDate(selectedYear?.start_date)}
            taxYearEndDateUser={toApiDate(selectedYear?.end_date)}
          />
          <Typography
            variant="caption"
            sx={{ color: leavePalette.subText, pl: 1, pb: 1, display: "block" }}
          >
            {totalPermission} permission(s) taken this year
          </Typography>
        </Box>
      )}

      {/* HISTORY */}
      {tab === 2 && (
        <Box sx={{ ...cardSx, p: { xs: 1.5, md: 2.5 } }}>
          <LeaveHistoryTable rows={leaveRequests} onRowClick={() => {}} />
        </Box>
      )}
        </Box>
      </Box>
    </Box>
  );
};
